import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Loader2, Plus, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { apiClient } from '../../lib/api-client';
import { buildLeafDeleteRequestBody, postLeafDeleteJson } from '../../lib/leaf-delete-request';
import { EntityEditDialog } from './EntityEditDialog';
import {
  parseEntityDefinitionsFromStreamYaml,
  stripManageEntitySourceFields,
} from './manage-entity-definitions.utils';
import {
  ManageEntityStreamFilterPills,
  streamLabelForId,
  type ManageEntityStreamFilter,
} from './ManageEntityStreamPicker';

interface ProductItem {
  id: string;
  displayName?: string;
}

type ProductRow = ProductItem & { sourceStreamId: string; sourceStreamLabel: string };

interface ImpactResource {
  type: string;
  name: string;
  shared: boolean;
  metadata?: { appName?: string; teamName?: string; chatName?: string; channelId?: string; channelKind?: string };
}

interface DeleteImpact {
  rolesToDelete: Array<{
    code: string;
    agentTitle: string;
    resources?: ImpactResource[];
    leafScope?: { product: string; teamType: string; tenantCode: string };
  }>;
  rolesToUpdate: Array<{ code: string; agentTitle: string; refsRemoved: string[] }>;
  sharedResources: Array<{ type: string; name: string; sharedWith: string[] }>;
}

interface ManageProductsDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  streamId: string;
  streamOptions?: Array<{ streamId: string; displayName: string }>;
  onStreamIdChange?: (streamId: string) => void;
}

export function ManageProductsDialog({
  open,
  onClose,
  onChanged,
  streamId,
  streamOptions = [],
  onStreamIdChange,
}: ManageProductsDialogProps) {
  const resolvedOptions = useMemo(() => {
    if (streamOptions.length > 0) return streamOptions;
    if (streamId) return [{ streamId, displayName: streamId }];
    return [];
  }, [streamOptions, streamId]);

  const [listFilter, setListFilter] = useState<ManageEntityStreamFilter>('');
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newId, setNewId] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [adding, setAdding] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; streamId: string } | null>(null);
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResourceKeys, setDeleteResourceKeys] = useState<Set<string>>(() => new Set());

  const resourceKey = (roleCode: string, r: ImpactResource) =>
    `${roleCode}|${r.type}|${r.name}|${JSON.stringify(r.metadata || {})}`;

  const [editTarget, setEditTarget] = useState<ProductRow | null>(null);

  const addTargetStreamId = useMemo(() => {
    if (resolvedOptions.length === 0) return '';
    if (resolvedOptions.length === 1) return resolvedOptions[0].streamId;
    if (listFilter === 'all') return '';
    return listFilter;
  }, [resolvedOptions, listFilter]);

  useLayoutEffect(() => {
    if (!open) {
      setListFilter('');
      return;
    }
    if (resolvedOptions.length === 0) {
      setListFilter('');
      return;
    }
    if (resolvedOptions.length === 1) {
      setListFilter(resolvedOptions[0].streamId);
      return;
    }
    const valid = streamId && resolvedOptions.some((o) => o.streamId === streamId);
    setListFilter(valid ? streamId : 'all');
  }, [open, streamId, resolvedOptions]);

  const fetchProducts = useCallback(async () => {
    if (!open || resolvedOptions.length === 0) return;
    if (resolvedOptions.length > 1 && listFilter === '') return;
    setLoading(true);
    setError(null);
    try {
      const labelOpts = streamOptions.length > 0 ? streamOptions : resolvedOptions;
      if (resolvedOptions.length === 1) {
        const sid = resolvedOptions[0].streamId;
        const result = await apiClient.getAiConfigStreamYaml(sid);
        const defs = parseEntityDefinitionsFromStreamYaml<ProductItem>(result.yaml, 'productDefinitions');
        const label = streamLabelForId(labelOpts, sid);
        setProducts(defs.map((d) => ({ ...d, sourceStreamId: sid, sourceStreamLabel: label })));
        return;
      }
      if (listFilter === 'all') {
        const chunks = await Promise.all(
          resolvedOptions.map(async (opt) => {
            const result = await apiClient.getAiConfigStreamYaml(opt.streamId);
            const defs = parseEntityDefinitionsFromStreamYaml<ProductItem>(result.yaml, 'productDefinitions');
            const label = opt.displayName?.trim() || opt.streamId;
            return defs.map((d) => ({ ...d, sourceStreamId: opt.streamId, sourceStreamLabel: label }));
          }),
        );
        const flat = chunks.flat();
        flat.sort(
          (a, b) =>
            a.sourceStreamLabel.localeCompare(b.sourceStreamLabel) || a.id.localeCompare(b.id),
        );
        setProducts(flat);
        return;
      }
      const sid = listFilter;
      const result = await apiClient.getAiConfigStreamYaml(sid);
      const defs = parseEntityDefinitionsFromStreamYaml<ProductItem>(result.yaml, 'productDefinitions');
      const label = streamLabelForId(labelOpts, sid);
      setProducts(defs.map((d) => ({ ...d, sourceStreamId: sid, sourceStreamLabel: label })));
    } catch (err: any) {
      setError(err.message || 'Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [open, resolvedOptions, listFilter, streamOptions]);

  useEffect(() => {
    if (open) void fetchProducts();
  }, [open, fetchProducts]);

  useEffect(() => {
    setDeleteTarget(null);
    setImpact(null);
    setEditTarget(null);
    setNewId('');
    setNewDisplayName('');
  }, [listFilter, streamId]);

  const handleFilterChange = (next: ManageEntityStreamFilter) => {
    setListFilter(next);
    if (next !== 'all' && onStreamIdChange) onStreamIdChange(next);
  };

  const handleAdd = async () => {
    if (!newId.trim() || !addTargetStreamId) return;
    setAdding(true);
    setError(null);
    try {
      await apiClient.addAiConfigProduct(addTargetStreamId, {
        id: newId.trim(),
        displayName: newDisplayName.trim() || undefined,
      });
      setNewId('');
      setNewDisplayName('');
      await fetchProducts();
      onChanged();
    } catch (err: any) {
      setError(err.message || 'Failed to add product');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteClick = async (id: string, sid: string) => {
    setError(null);
    setImpact(null);
    setDeleteResourceKeys(new Set());
    setDeleteTarget({ id, streamId: sid });
    setImpactLoading(true);
    try {
      const result = await apiClient.getAiConfigDeleteImpact(sid, 'product', id);
      setImpact(result);
      const next = new Set<string>();
      for (const role of result.rolesToDelete || []) {
        for (const r of role.resources || []) {
          if (!r.shared) next.add(resourceKey(role.code, r));
        }
      }
      setDeleteResourceKeys(next);
    } catch (err: any) {
      setError(err.message || 'Failed to analyze delete impact');
      setDeleteTarget(null);
    } finally {
      setImpactLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !impact) return;
    setDeleting(true);
    setError(null);
    try {
      const sid = deleteTarget.streamId;
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';

      for (const role of impact.rolesToDelete) {
        const scope = role.leafScope;
        const resources = (role.resources || []).filter((r) => deleteResourceKeys.has(resourceKey(role.code, r)));
        if (!scope || resources.length === 0) continue;
        const body = buildLeafDeleteRequestBody(resources);
        try {
          await postLeafDeleteJson(apiBase, sid, scope.product, scope.teamType, scope.tenantCode, body);
        } catch (e: any) {
          throw new Error(e?.message || `Leaf delete failed for role ${role.code}`);
        }
      }

      for (const role of impact.rolesToDelete) {
        await apiClient.deleteAiConfigRole(sid, role.code);
      }

      await apiClient.deleteAiConfigProduct(sid, deleteTarget.id);
      setDeleteTarget(null);
      setImpact(null);
      setDeleteResourceKeys(new Set());
      await fetchProducts();
      onChanged();
    } catch (err: any) {
      setError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setImpact(null);
    setDeleteResourceKeys(new Set());
  };

  const showStreamFilter = resolvedOptions.length > 1;

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent maxWidth="2xl">
          <DialogHeader>
            <DialogTitle>Manage Products</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {resolvedOptions.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No stream selected. Create a stream first via Manage Streams.
              </p>
            )}

            {resolvedOptions.length > 0 && showStreamFilter && (
              <ManageEntityStreamFilterPills
                streamOptions={resolvedOptions}
                activeFilter={listFilter}
                onActiveFilterChange={handleFilterChange}
              />
            )}

            {error && resolvedOptions.length > 0 && (
              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            )}

            {resolvedOptions.length === 0 ? null : loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {products.map((p) => (
                    <div
                      key={`${p.sourceStreamId}:${p.id}`}
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      <div className="min-w-0 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="shrink-0 font-normal">
                          {p.sourceStreamLabel}
                        </Badge>
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {p.displayName || p.id}
                          </span>
                          {p.displayName && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {p.id}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setEditTarget(p)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 dark:text-red-400"
                          onClick={() => handleDeleteClick(p.id, p.sourceStreamId)}
                          disabled={
                            deleteTarget?.id === p.id &&
                            deleteTarget?.streamId === p.sourceStreamId &&
                            impactLoading
                          }
                        >
                          {deleteTarget?.id === p.id &&
                          deleteTarget?.streamId === p.sourceStreamId &&
                          impactLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {products.length === 0 && (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                      No products defined
                      {listFilter === 'all' ? ' across these streams' : ''}
                    </p>
                  )}
                </div>

                {deleteTarget && impact && (
                  <div className="mb-4 p-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      <span className="font-semibold text-sm text-amber-800 dark:text-amber-200">
                        Delete product &ldquo;{deleteTarget.id}&rdquo; from {deleteTarget.streamId}?
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      Roles listed below will be removed from YAML. Select external resources to deprovision first.
                    </p>
                    {impact.rolesToUpdate.length > 0 && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        Other roles will only lose this product from scope (YAML update only):{' '}
                        {impact.rolesToUpdate.map((r) => r.code).join(', ')}
                      </p>
                    )}
                    {impact.rolesToDelete.length > 0 && (
                      <div className="mb-2 space-y-2 max-h-48 overflow-y-auto">
                        {impact.rolesToDelete.map((r) => (
                          <div key={r.code} className="border border-amber-200/80 dark:border-amber-800/50 rounded p-2 bg-white/60 dark:bg-gray-900/40 text-xs">
                            <p className="font-medium text-gray-800 dark:text-gray-200">
                              {r.agentTitle} <span className="font-mono text-gray-500">({r.code})</span>
                            </p>
                            {(r.resources || []).length === 0 ? (
                              <p className="text-[11px] text-gray-500 mt-1">No leaf resources mapped.</p>
                            ) : (
                              <ul className="mt-1 space-y-1">
                                {(r.resources || []).map((res) => {
                                  const key = resourceKey(r.code, res);
                                  return (
                                    <li key={key} className="flex items-start gap-2">
                                      <input
                                        type="checkbox"
                                        className="mt-0.5 rounded border-gray-300"
                                        checked={deleteResourceKeys.has(key)}
                                        disabled={deleting}
                                        onChange={() => {
                                          setDeleteResourceKeys((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(key)) next.delete(key);
                                            else next.add(key);
                                            return next;
                                          });
                                        }}
                                      />
                                      <span>
                                        <span className="font-mono">{res.type}</span> {res.name}
                                        {res.shared ? <span className="text-amber-700 dark:text-amber-400"> (shared)</span> : null}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {impact.sharedResources.length > 0 && (
                      <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc list-inside mb-2">
                        {impact.sharedResources.map((r, i) => (
                          <li key={i}>
                            {r.type}: {r.name} (shared across roles)
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex gap-2">
                      <Button variant="destructive" size="sm" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                        Confirm
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleCancelDelete} disabled={deleting}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                    Add Product
                  </p>
                  {!addTargetStreamId ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Select a single stream above to add a product (not &ldquo;All&rdquo;).
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <Input
                      placeholder="Product ID"
                      value={newId}
                      onChange={(e) => setNewId(e.target.value)}
                      className="text-sm min-w-[120px] flex-1"
                    />
                    <Input
                      placeholder="Display name (optional)"
                      value={newDisplayName}
                      onChange={(e) => setNewDisplayName(e.target.value)}
                      className="text-sm min-w-[140px] flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={handleAdd}
                      disabled={adding || !newId.trim() || !addTargetStreamId}
                    >
                      {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editTarget && (
        <EntityEditDialog
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            void fetchProducts();
            onChanged();
          }}
          streamId={editTarget.sourceStreamId}
          entityType="product"
          entityId={editTarget.id}
          entityConfig={stripManageEntitySourceFields(editTarget)}
        />
      )}
    </>
  );
}
